if (!customElements.get('cart-drawer')) {
  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.setHeaderCartIconAccessibility();
      // A bfcache back/forward restore revives the drawer (or its deferred
      // template) with markup from before the cart changed on another page -
      // opening it then shows deleted items or a stale rewards bar.
      window.addEventListener('pageshow', (event) => {
        // BDCart (bd-cart-core.js) re-syncs on bfcache restore itself; this stays as the fallback.
        if (event.persisted && !window.BDCart) this.reload();
      });
    }

    init() {
      if (!this.isInitialized) {
        window.loadTemplateContent(this);

        requestAnimationFrame(() => {
          // Bind various things
          this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
          const overlay = this.querySelector('#CartDrawer-Overlay');
          overlay.__bdBound = true;
          overlay.addEventListener('click', this.close.bind(this));
          this.moveCssLinks();
          this.isInitialized = true;
        });
      }
    }

    reload() {
      // Reloading while the drawer body is still inside its deferred <template>
      // finds no #CartDrawer to render into and dies on a null .drawer__inner —
      // the drawer then reopens later showing stale page-load markup.
      if (!this.isInitialized) this.init();
      if (this._reloading) { this._reloadAgain = true; return; }
      this._reloading = true;
      const sections = this.getSectionsToRender().map((section) => section.id);
      fetch(`?sections=${sections}`)
        .then((response) => response.json())
        .then((response) => {
          this.renderContents({
            sections: {
              ...response
            }
          }, true);
        })
        .catch((e) => {
          console.error(e);
        })
        .finally(() => {
          this._reloading = false;
          if (this._reloadAgain) { this._reloadAgain = false; this.reload(); }
        });
    }

    moveCssLinks() {
      const cssLinks = this.querySelectorAll('link[rel="stylesheet"][href*=".css"]');
      cssLinks.forEach(link => {
        // Check if the link already exists in the document head
        const href = link.href;
        if (!document.head.querySelector(`link[href="${href}"]`)) {
          // Clone the link element
          const newLink = link.cloneNode(true);
          // Append the cloned link to the document head
          document.head.appendChild(newLink);
        }
      });
    }

    setHeaderCartIconAccessibility() {
      const cartLink = document.querySelector('#cart-icon-bubble');
      if (!cartLink) return;

      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }

    open(triggeredBy) {
      this.init();

      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
      // here the animation doesn't seem to always get triggered. A timeout seem to help
      setTimeout(() => {
        this.classList.add('animate', 'active');
      });

      this.removeDeleteTabIndex();

      this.addEventListener(
        'transitionend',
        () => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          trapFocus(containerToTrapFocusOn, focusElement);

          publish(PUB_SUB_EVENTS.cartOpen);
        },
        {once: true}
      );

      document.body.classList.add('overflow-hidden');
      this.lockScroll();
    }

    /* iOS Safari ignores overflow:hidden on <body> for touch scrolling, and a naive lock jumps the page
       to the top. Pin the body where it is (position:fixed at -scrollY) and restore it on close. */
    lockScroll() {
      if (this._scrollLocked) return;
      this._scrollLocked = true;
      this._lockY = window.scrollY || window.pageYOffset || 0;
      const b = document.body;
      b.style.position = 'fixed';
      b.style.top = `-${this._lockY}px`;
      b.style.left = '0';
      b.style.right = '0';
      b.style.width = '100%';
    }

    unlockScroll() {
      if (!this._scrollLocked) return;
      this._scrollLocked = false;
      const b = document.body;
      b.style.position = '';
      b.style.top = '';
      b.style.left = '';
      b.style.right = '';
      b.style.width = '';
      window.scrollTo({ top: this._lockY, left: 0, behavior: 'instant' });
    }

    close() {
      this.classList.remove('active');
      removeTrapFocus(this.activeElement);
      document.body.classList.remove('overflow-hidden');
      this.unlockScroll();
      publish(PUB_SUB_EVENTS.cartClose);
    }

    /**
     * Ensure all remove buttons can be tabbed to
     */
    removeDeleteTabIndex() {
      this.querySelectorAll('.cart-remove-button').forEach((button) => {
        button.removeAttribute('tabindex')
      });
    }

    setSummaryAccessibility(cartDrawerNote) {
      cartDrawerNote.setAttribute('role', 'button');
      cartDrawerNote.setAttribute('aria-expanded', 'false');

      if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
        cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
      }

      cartDrawerNote.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
      });

      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }

    renderContents(parsedState, preventOpen = false) {
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) drawerInner.classList.remove('is-empty');
      this.productId = parsedState.id;
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement || !parsedState.sections[section.id]) return;
        if (window.BDCart) {
          // update in place: keeps scroll position, open panels, the shipping calculator, the rewards
          // bar and the protection buttons instead of rebuilding the whole drawer
          const html = parsedState.sections[section.id].replace(/<template\b[^>]*>|<\/template>/g, '');
          const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector || '.shopify-section');
          if (fresh) { window.BDCart.morphInto(sectionElement, fresh); return; }
        }
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      });

      // The empty-state class sits on <cart-drawer> itself, outside #CartDrawer,
      // so the section swap above never updates it - mirror it from the fresh markup.
      const drawerSection = parsedState.sections && parsedState.sections['cart-drawer'];
      if (drawerSection) {
        const freshDrawer = this.getSectionDOM(
          drawerSection.replace(/<template\b[^>]*>|<\/template>/g, ''),
          'cart-drawer'
        );
        if (freshDrawer) this.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
      }

      setTimeout(() => {
        window.loadTemplateContent(this);
        const overlay = this.querySelector('#CartDrawer-Overlay');
        if (overlay && !overlay.__bdBound) {
          overlay.__bdBound = true;
          overlay.addEventListener('click', this.close.bind(this));
        }
      });

      this.removeDeleteTabIndex();

      if (theme.settings.cartDrawer.atcAction === 'drawer' && !preventOpen) {
        setTimeout(() => {
          this.open();
        });
      }
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      html = html.replace(/<template\b[^>]*>|<\/template>/g, '');
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }

    getSectionsToRender() {
      return [
        {
          id: 'cart-drawer',
          selector: '#CartDrawer',
        },
        {
          id: 'cart-icon-bubble',
        },
      ];
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }
  }

  customElements.define('cart-drawer', CartDrawer);
}

if (!customElements.get('cart-drawer-items')) {
  customElements.whenDefined('cart-items').then(() => {
    class CartDrawerItems extends CartItems {
      getSectionsToRender() {
        return [
          {
            id: 'CartDrawer',
            section: 'cart-drawer',
            selector: '.drawer__inner',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }
    }

    customElements.define('cart-drawer-items', CartDrawerItems);
  });
}
